// Cookie-based API client. Sends `credentials: "include"` so the
// `session_token` HttpOnly cookie set by BE-003's login flow rides
// every request automatically (spec §4.1.1, §4.1.2). No bearer-token
// handling — the cookie is the session, full stop.
//
// Error shape: thrown ApiError carries status + parsed body for the
// caller to branch on. Domain endpoints will layer typed wrappers on
// top of this in their respective phases.
//
// Session lifecycle (Settings-Sec):
//   * On 401 from a non-/api/v1/auth/* path, the client attempts ONE
//     /api/v1/auth/refresh and retries the original request once. A
//     module-level promise dedupes concurrent refreshes so a burst of
//     401s only fires a single round-trip to the server.
//   * If refresh fails (or the retried request still 401s), the
//     registered auth-lost handler runs — typically AuthProvider
//     clears its query cache and routes the user to /login. There is
//     no JWT here; the cookie is opaque, so "refresh" means "rotate
//     the server-side session row and re-evaluate User.is_active +
//     Membership status".

import { env } from "@/lib/env";

export interface ApiErrorBody {
  // Every backend error is `{"detail": "<code>"}` (Django Ninja, enforced in
  // backend/config/api_errors.py). Some errors carry extra top-level keys
  // (e.g. the CSV-import `missing_columns` array), hence the index signature.
  detail?: string;
  [key: string]: unknown;
}

// FE-A: the active broker entity id, set by ``AuthProvider`` once the
// user picks a brokerage. The API client injects it as
// ``X-Entity-Id`` on every request so the tenant-resolution
// middleware doesn't have to fall back to the default-broker
// lookup. Module-level mutable state is on purpose: every request
// goes through the same ``apiRequest`` so a single setter keeps the
// SPA in sync without threading the value through every call site.
let _currentEntityId: string | null = null;

export function setCurrentEntityId(id: string | null): void {
  _currentEntityId = id;
}

export function getCurrentEntityId(): string | null {
  return _currentEntityId;
}

// ── Session refresh & auth-lost notification ──────────────────────────────
//
// The refresh promise is module-scoped so any number of concurrent
// 401s collapse onto a single network call. Cleared in `finally` so
// a later 401 (after the cookie is rotated again) can refresh anew.

type AuthLostHandler = () => void;

let _refreshPromise: Promise<boolean> | null = null;
let _authLostHandler: AuthLostHandler | null = null;

export function setAuthLostHandler(fn: AuthLostHandler | null): void {
  _authLostHandler = fn;
}

export function notifyAuthLost(): void {
  const handler = _authLostHandler;
  if (handler) handler();
}

// Exported so AuthProvider can fire a proactive refresh on a timer
// and tests can assert single-flight behavior. Returns `true` only if
// the server replied 200; any other status (including 401 from a
// revoked session) returns `false`.
export function refreshSession(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  const url = `${env.apiUrl.replace(/\/$/, "")}/api/v1/auth/refresh`;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      return res.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

// Test-only escape hatch. Resets the single-flight gate between
// tests; production code never calls this.
export function _resetRefreshStateForTests(): void {
  _refreshPromise = null;
  _authLostHandler = null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
    message?: string,
  ) {
    super(message ?? body.detail ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

// Internal flag carried only on the recursive retry call; never set
// by callers. Prevents a 401-on-retry from looping back into refresh.
interface InternalRequestOptions extends RequestOptions {
  _retried?: boolean;
}

function isAuthPath(path: string): boolean {
  return path.startsWith("/api/v1/auth/");
}

export async function apiRequest<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  return _apiRequest<T>(path, opts as InternalRequestOptions);
}

async function _apiRequest<T>(
  path: string,
  opts: InternalRequestOptions,
): Promise<T> {
  const url = `${env.apiUrl.replace(/\/$/, "")}${path}`;
  // ``FormData`` callers (multipart upload, e.g. Touchpoint
  // recordings) need the browser to set the ``multipart/form-data``
  // boundary header itself — pinning ``Content-Type: application/json``
  // would scramble the parser on the BE. Same logic for ``Blob`` /
  // ``ArrayBuffer`` if a future caller streams raw bytes.
  const isMultipart = (
    typeof FormData !== "undefined" && opts.body instanceof FormData
  );
  const headers: Record<string, string> = {
    ...(isMultipart ? {} : { "Content-Type": "application/json" }),
    ...(opts.headers ?? {}),
  };
  // Auth endpoints (``/api/v1/auth/*``) are explicitly carved out
  // from the tenant-resolution middleware, so injecting an entity
  // header would be wasted bytes. Every other request rides the
  // currently-selected broker entity from the AuthProvider.
  if (
    _currentEntityId &&
    !headers["X-Entity-Id"] &&
    !isAuthPath(path)
  ) {
    headers["X-Entity-Id"] = _currentEntityId;
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    headers,
    signal: opts.signal,
  };

  if (opts.body !== undefined) {
    init.body = isMultipart
      ? (opts.body as FormData)
      : JSON.stringify(opts.body);
  }

  const response = await fetch(url, init);

  // Session-rotation hook: a 401 on a tenant-scoped path may just
  // mean the cookie was rotated server-side or expired between when
  // the page loaded and now. Try a single refresh + retry. We never
  // refresh on auth endpoints themselves — /me 401 is the canonical
  // "not logged in" signal and refreshing it would loop a request
  // that has no business being retried anonymously.
  if (
    response.status === 401 &&
    !opts._retried &&
    !isAuthPath(path)
  ) {
    const ok = await refreshSession();
    if (ok) {
      return _apiRequest<T>(path, { ...opts, _retried: true });
    }
    // Refresh definitively failed — the session is dead. Tell the
    // app so it can clear state and redirect to /login. The throw
    // below still happens; callers that wrap `apiRequest` in their
    // own try/catch see the original 401.
    notifyAuthLost();
  }

  // 204 No Content has no body to parse.
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as ApiErrorBody | T)
    : ((await response.text()) as unknown as T);

  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiErrorBody);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    apiRequest<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "DELETE" }),
};
